import { apiClient } from '../lib/apiClient';

/** Quarantine (Reef Guard #1826): protocols with ordered steps; each step has a reminder task. No doses. */
export const PROTOCOL_TYPES = ['copper', 'ttm', 'fallow', 'coral', 'observation'] as const;
export type ProtocolType = (typeof PROTOCOL_TYPES)[number];

/** Step keys per protocol (mirrors src/services/quarantine/protocols.ts) — for the localized reminder task titles. */
export const PROTOCOL_STEP_KEYS: Readonly<Record<ProtocolType, readonly string[]>> = {
  copper: ['copper_start', 'copper_end', 'prazi', 'move_to_display'],
  ttm: ['ttm_move_1', 'ttm_move_2', 'ttm_move_3', 'ttm_move_4'],
  fallow: ['fallow_start', 'fallow_end'],
  coral: ['coral_observe', 'coral_dip_1', 'coral_dip_2', 'coral_dip_3', 'move_to_display'],
  observation: ['observe', 'move_to_display'],
};

/** Which protocols fit an animal of this category (fallow is for the display tank, not an animal). */
export function protocolsFor(category: 'coral' | 'fish' | 'invertebrate' | 'other'): ProtocolType[] {
  if (category === 'fish') return ['copper', 'ttm', 'observation'];
  if (category === 'coral') return ['coral', 'observation'];
  return ['observation'];
}

export interface QuarantineStep {
  readonly id: string;
  readonly sequence: number;
  readonly key: string;
  readonly dueDate: string;
  readonly completed: boolean;
  readonly completedAt: string | null;
  readonly taskId: string | null;
  readonly available: boolean;
}

export interface QuarantineProtocol {
  readonly id: string;
  readonly aquariumId: string;
  readonly livestockId: string | null;
  readonly livestockName: string | null;
  readonly protocolType: ProtocolType;
  readonly status: 'active' | 'completed' | 'aborted';
  readonly confidence: 'high' | 'medium' | 'none';
  readonly source: string;
  readonly config: { readonly dipIntervalDays?: number };
  readonly startedAt: string;
  readonly finishedAt: string | null;
  readonly steps: QuarantineStep[];
  /** The animal's treatment sensitivities (#1830); absent on older responses. */
  readonly sensitivities?: TreatmentSensitivity[];
}

/** A fish group that does not tolerate a medication (#1830): copper-sensitive or CP-intolerant. */
export interface TreatmentSensitivity {
  readonly treatment: 'copper' | 'cp';
  readonly group: 'tangs' | 'wrasses' | 'puffers' | 'flasher_wrasses' | 'coris_wrasses' | 'pseudocheilinus';
  readonly confidence: 'medium' | 'high';
  readonly source: string;
}

const base = (aquariumId: string) => `/api/v1/aquariums/${aquariumId}/quarantine`;
export const quarantineKey = (aquariumId: string) => ['quarantine', aquariumId] as const;

export async function listQuarantine(aquariumId: string): Promise<QuarantineProtocol[]> {
  const res = await apiClient.get<{ data: QuarantineProtocol[] }>(base(aquariumId));
  return Array.isArray(res?.data) ? res.data : [];
}

export async function startQuarantine(
  aquariumId: string,
  input: { protocol_type: ProtocolType; livestock_id?: string | null; dip_interval_days?: number; task_titles: Record<string, string> },
): Promise<QuarantineProtocol> {
  return (await apiClient.post<{ data: QuarantineProtocol }>(base(aquariumId), input)).data;
}

export async function completeQuarantineStep(aquariumId: string, protocolId: string, stepId: string): Promise<QuarantineProtocol> {
  return (await apiClient.post<{ data: QuarantineProtocol }>(`${base(aquariumId)}/${protocolId}/steps/${stepId}/complete`, {})).data;
}

export async function endQuarantine(aquariumId: string, protocolId: string, outcome: 'finish' | 'abort'): Promise<QuarantineProtocol> {
  return (await apiClient.post<{ data: QuarantineProtocol }>(`${base(aquariumId)}/${protocolId}/${outcome}`, {})).data;
}

export const quarantineSensitivityKey = (aquariumId: string, livestockId: string) => ['quarantine', aquariumId, 'sensitivity', livestockId] as const;

/** GET …/quarantine/sensitivity/:livestockId — copper / CP sensitivities of one animal (#1830). */
export async function getQuarantineSensitivity(aquariumId: string, livestockId: string): Promise<TreatmentSensitivity[]> {
  const res = await apiClient.get<{ data: TreatmentSensitivity[] }>(`${base(aquariumId)}/sensitivity/${encodeURIComponent(livestockId)}`);
  return Array.isArray(res?.data) ? res.data : [];
}

export async function skipQuarantine(aquariumId: string, livestockId: string): Promise<void> {
  await apiClient.post(`${base(aquariumId)}/skip`, { livestock_id: livestockId });
}
